// ============================================================================
// firebase-brand.js — frontend read access to the brand single-source-of-truth.
//
// Reads config/brand (world-readable under Stage-1 Firestore rules) and merges
// it over brandDefaults so callers always get a fully-populated object. Writes
// do NOT happen here — they go through the password-gated backend
// (POST /api/studio/brand) so a random signed-in user can't rebrand the site.
// ============================================================================

import { db, storage } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { brandDefaults, mergeBrand } from '../config/brandDefaults';

const brandDocRef = () => doc(db, 'config', 'brand');

// One-shot read. Falls back to defaults on any error (offline, missing doc).
export async function loadBrand() {
  try {
    const snap = await getDoc(brandDocRef());
    return mergeBrand(snap.exists() ? snap.data() : null);
  } catch (e) {
    console.warn('loadBrand: falling back to defaults', e);
    return mergeBrand(null);
  }
}

// Live subscription. Calls cb(mergedBrand) now-ish and on every change.
// Returns an unsubscribe fn. Emits defaults immediately if the read errors.
export function subscribeBrand(cb) {
  try {
    return onSnapshot(
      brandDocRef(),
      (snap) => cb(mergeBrand(snap.exists() ? snap.data() : null)),
      (e) => {
        console.warn('subscribeBrand error, using defaults', e);
        cb(mergeBrand(null));
      }
    );
  } catch (e) {
    console.warn('subscribeBrand threw, using defaults', e);
    cb(mergeBrand(null));
    return () => {};
  }
}

// Upload a replacement brand image to Firebase Storage and return its public
// download URL. `slot` (e.g. "og", "favicon") only shapes the filename.
export async function uploadBrandImage(file, slot = 'image') {
  if (!file) throw new Error('No file');
  const ext = ((file.name || '').split('.').pop() || 'png')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') || 'png';
  const path = `brand/${slot}-${Date.now()}.${ext}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type || 'image/png' });
  const url = await getDownloadURL(ref);
  // cache-bust so social scrapers / browsers fetch the new pixels
  return url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();
}

// Resize an image file to a square PNG blob of `size` px (client-side canvas).
async function resizeSquarePng(file, size) {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  // contain the source, centered
  const scale = Math.min(size / bitmap.width, size / bitmap.height);
  const w = bitmap.width * scale, h = bitmap.height * scale;
  ctx.drawImage(bitmap, (size - w) / 2, (size - h) / 2, w, h);
  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
}

// Generate the full icon set (favicon + 192 + 512) from one master image,
// upload each to Storage, and return the URLs to write into config.assets.
export async function generateIconSet(file) {
  const sizes = [
    ['faviconUrl', 48],
    ['logo192Url', 192],
    ['logo512Url', 512],
  ];
  const out = {};
  for (const [key, size] of sizes) {
    const blob = await resizeSquarePng(file, size);
    const ref = storageRef(storage, `brand/icon-${size}-${Date.now()}.png`);
    await uploadBytes(ref, blob, { contentType: 'image/png' });
    const url = await getDownloadURL(ref);
    out[key] = url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();
  }
  return out; // { faviconUrl, logo192Url, logo512Url }
}

export { brandDefaults };
